import * as React from "react";

import {
  Fieldset,
  Radio,
  RadioGroup,
  TagInput,
} from "@/shared/foundation/primitives";

import { ParameterError } from "./ParameterError";
import styles from "./parameterForms.module.css";
import { splitList, useParameterRecord } from "./parameterValues";

type TopicsMode = "all" | "only" | "except";

export interface TopicsControlProps {
  name: string;
  /** The comma-separated list of topics to keep. */
  includeKey: string;
  /** The comma-separated list of topics to leave out, when the action has one. */
  excludeKey?: string;
}

/**
 * Which topics an ingestion extracts: all of them, only the listed ones, or
 * all but the listed ones. Only one of the two keys is ever written; choosing
 * a mode moves the list to its key and clears the other.
 */
export function TopicsControl({
  name,
  includeKey,
  excludeKey,
}: TopicsControlProps) {
  const record = useParameterRecord(name);
  const legendId = React.useId();
  const included = splitList(record.get(includeKey));
  const excluded =
    excludeKey === undefined ? [] : splitList(record.get(excludeKey));
  let stored: TopicsMode = "all";
  if (excluded.length > 0) {
    stored = "except";
  } else if (included.length > 0) {
    stored = "only";
  }
  // A mode picked with no topics typed yet has nothing stored to say so.
  const [picked, setPicked] = React.useState<TopicsMode | null>(null);
  const mode = picked ?? stored;
  const topics = mode === "except" ? excluded : included;

  const writeTopics = (next: TopicsMode, list: readonly string[]) => {
    const joined = list.join(",");
    record.write({
      [includeKey]: next === "only" ? joined : undefined,
      ...(excludeKey === undefined
        ? {}
        : { [excludeKey]: next === "except" ? joined : undefined }),
    });
  };

  return (
    <Fieldset.Root data-testid="param-topics">
      <Fieldset.Legend id={legendId}>Topics</Fieldset.Legend>
      <RadioGroup<TopicsMode>
        aria-labelledby={legendId}
        value={mode}
        onValueChange={(next) => {
          setPicked(next);
          writeTopics(next, next === "all" ? [] : topics);
        }}
      >
        <Radio value="all" label="All topics" />
        <Radio value="only" label="Only these topics" />
        {excludeKey !== undefined && (
          <Radio value="except" label="All except these topics" />
        )}
      </RadioGroup>
      {mode !== "all" && (
        <div className={styles.nested}>
          <TagInput
            tags={topics}
            onChange={(list) => {
              writeTopics(mode, list);
            }}
            placeholder="/camera/image_raw"
            size="lg"
            aria-labelledby={legendId}
          />
        </div>
      )}
      <ParameterError message={record.error(includeKey)} />
      {excludeKey !== undefined && (
        <ParameterError message={record.error(excludeKey)} />
      )}
    </Fieldset.Root>
  );
}
